import { Injectable, OnDestroy, computed, inject, signal } from '@angular/core';
import { take } from 'rxjs';
import { AppConstants } from 'src/app/app-constants';
import {
  AccuracyFeedbackState,
  IAccuracyClaim,
} from 'src/app/interfaces/iaccuracy-feedback';
import { IFlattenedMessageAdherenceSection } from 'src/app/interfaces/iflattenedMessageAdherenceSection';
import { IObjectiveConversational } from 'src/app/interfaces/iobjective-conversational';
import { ISkillDetail } from 'src/app/interfaces/iskill-detail';
import {
  IResultsConversational,
  IWeightAverage,
} from 'src/app/interfaces/iresults-conversational';
import { IScoreMessageAdherence } from 'src/app/interfaces/iscore-message-adherence';
import { IScoreMessageAdherenceV2 } from 'src/app/interfaces/iscore-message-adherence-v2';
import { IScoreOverallConversational } from 'src/app/interfaces/iscore-overall-conversational';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { LocaleService } from 'src/app/services/locale.service';
import { ResultsConversationalService } from 'src/app/services/learner/results-conversational.service';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { IUserTranscriptResponse } from 'src/app/interfaces/iuser-transcript-response';
import {
  ScoreArrayItem,
  ScoreKind,
  ScoreKey,
} from 'src/app/utils/enums/result/resultConversational.enum';

@Injectable()
export class PathwayRoleplayResultsService implements OnDestroy {
  readonly resultReady = signal(false);
  readonly isTimedOut = signal(false);
  readonly hasError = signal(false);
  readonly conversationalResults = signal<IResultsConversational | undefined>(
    undefined,
  );
  readonly scoreOverall = signal<IScoreOverallConversational | undefined>(
    undefined,
  );
  readonly methodology = signal<IObjectiveConversational[] | undefined>(
    undefined,
  );
  readonly skills = signal<ISkillDetail[] | undefined>(undefined);
  readonly userTranscript = signal<IUserTranscriptResponse | undefined>(
    undefined,
  );
  readonly scoreMessageAdherence = signal<IScoreMessageAdherence[]>([]);
  readonly flattenedSections = signal<IFlattenedMessageAdherenceSection[]>([]);
  readonly isMessageAdherenceV2 = signal(false);
  readonly scoresArray = signal<ScoreArrayItem[]>([]);
  readonly messageAdherenceScore = computed(
    () =>
      this.scoresArray().find((s) => s.labelKey === 'MESSAGE_ADHERENCE')
        ?.value ?? '',
  );
  readonly scoreCriteria = computed(() => {
    const r = this.conversationalResults();
    return r?.stepCompletionRequirement?.conditionValue ?? r?.moduleAssignmentScoreCriteria ?? 0;
  });

  readonly shouldFetchPromptsData = signal(true);

  readonly accuracyState = signal<AccuracyFeedbackState>(
    AccuracyFeedbackState.Loading,
  );
  readonly accuracyClaims = signal<IAccuracyClaim[]>([]);
  private readonly accuracyFeedbackFlagEnabled = signal(false);

  /** The single gate on the accuracy card. */
  readonly showAccuracy = computed(() => {
    const results = this.conversationalResults();
    return (
      this.accuracyFeedbackFlagEnabled() &&
      !!results?.accuracyFeedbackEnabled &&
      (results?.accuracyDocumentCount ?? 0) > 0
    );
  });

  private pollingInterval: ReturnType<typeof setInterval> | null = null;
  private practiceCallUuid = '';
  private readonly msDuration = AppConstants.RESULTS_POLLING_DURATION_MS;
  private startTime = 0;
  /** Resolves once the feature flag is known, so a fetch never races it. */
  private accuracyFlagResolved: Promise<void> = Promise.resolve();
  private accuracyFetchedForUuid = '';
  private readonly featureFlagsService = inject(FeatureFlagsService);

  constructor(
    private readonly resultsConversationalService: ResultsConversationalService,
    private readonly loggerService: LoggerService,
    private readonly localeService: LocaleService,
  ) {}

  init(practiceCallUuid: string): void {
    this.practiceCallUuid = practiceCallUuid;
    this.accuracyFlagResolved = this.resolveAccuracyVisibility();
    this.pollData();
  }

  private pollData(): void {
    this.getConversationalResults();
    this.startTime = Date.now();
    this.pollingInterval = setInterval(() => {
      const elapsed = Date.now() - this.startTime;
      if (elapsed >= this.msDuration) {
        this.isTimedOut.set(true);
        this.stopPolling();
      } else if (!this.resultReady()) {
        this.getConversationalResults();
      }
    }, AppConstants.RESULTS_POLLING_INTERVAL_MS);
  }

  private stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  private getConversationalResults(): void {
    if (!this.practiceCallUuid) return;

    this.resultsConversationalService
      .getResultsConversationalData(this.practiceCallUuid)
      .subscribe({
        next: (res: IResultsConversational) => {
          if (res?.moduleAssignmentUuid) {
            this.conversationalResults.set(res);
            this.stopPolling();
            if (this.shouldFetchPromptsData()) {
              this.fetchPromptsResults(res);
              this.fetchUserTranscript();
              // Scoring is done, so the accuracy row exists alongside the
              // other analysis rows.
              this.fetchAccuracyFeedback();
            }
            this.resultReady.set(true);
          }
        },
        error: (err) => {
          this.hasError.set(true);
          this.stopPolling();
          this.loggerService.error(
            '[ROLEPLAY RESULTS] getConversationalResults failed',
            err,
          );
        },
      });
  }

  private fetchPromptsResults(
    conversationalResults: IResultsConversational,
  ): void {
    const prompts = conversationalResults.promptTypes ?? [];

    prompts.forEach((prompt) => {
      if (
        prompt === AppConstants.CLEAN_TRANSCRIPT_PROMPT_TYPE
      ) {
        return;
      }

      this.resultsConversationalService
        .getResultsPromptTypeData(this.practiceCallUuid, prompt)
        .subscribe({
          next: (res: any) => {
            if (res?.contentDetails) {
              this.processPromptResult(
                prompt,
                res.contentDetails,
                conversationalResults,
              );
            }
            this.scoresArray.set(this.buildScoreArray(conversationalResults));
          },
          error: (err) => {
            this.loggerService.error(
              `[ROLEPLAY RESULTS] fetchPromptsResults [${prompt}] failed`,
              err,
            );
          },
        });
    });
  }

  private processPromptResult(
    prompt: string,
    contentDetails: string,
    conversationalResults: IResultsConversational,
  ): void {
    switch (prompt) {
      case AppConstants.SCORE_OVERALL_PROMPT_TYPE:
        this.scoreOverall.set(JSON.parse(contentDetails));
        break;
      case AppConstants.SCORE_CUSTOMIZED_PROMPT_TYPE: {
        const data = JSON.parse(contentDetails);
        const key = Object.keys(data)[0];
        this.methodology.set(data[key]);
        break;
      }
      case AppConstants.SCORE_SKILLS_PROMPT_TYPE: {
        const data = JSON.parse(contentDetails);
        const details: any[] = data['Skill Details'] ?? data['skilldetails'] ?? [];
        this.skills.set(details.map((s) => ({
          title: s.title,
          score: this.toSkillScore(s.score),
          feedback: s.feedback,
          actionItems: s['action_items'] ?? s['action items'] ?? [],
        })));
        break;
      }
      case AppConstants.SCORE_MESSEGE_ADHERENCE_PROMPT_TYPE: {
        const data = JSON.parse(contentDetails);
        const key = Object.keys(data)[0];
        this.scoreMessageAdherence.set(data[key]);
        break;
      }
      case AppConstants.SCORE_MESSAGE_ADHERENCE_V2_PROMPT_TYPE: {
        const data = JSON.parse(contentDetails);
        const key = Object.keys(data)[0];
        this.flattenedSections.set(this.transformMessageAdherenceV2(data[key]));
        this.isMessageAdherenceV2.set(true);
        break;
      }
    }
    this.scoresArray.set(this.buildScoreArray(conversationalResults));
  }

  private transformMessageAdherenceV2(
    v2: IScoreMessageAdherenceV2,
  ): IFlattenedMessageAdherenceSection[] {
    return v2.sections.map((section: any) => {
      const title = Object.keys(section)[0];
      const sectionData = section[title];
      return {
        title,
        positives: sectionData.facts.filter((f: any) => f.correct === 1),
        negatives: sectionData.facts.filter((f: any) => f.correct === 0),
        numIncludedCorrectly: sectionData.numIncludedCorrectly,
        totalNum: sectionData.totalNum,
      };
    });
  }

  private buildScoreArray(
    conversationalResults: IResultsConversational,
  ): ScoreArrayItem[] {
    const practiceScore = conversationalResults.practiceCall?.score ?? 0;
    const criteriaScore =
      conversationalResults.stepCompletionRequirement?.conditionValue ??
      conversationalResults.moduleAssignmentScoreCriteria ?? 0;

    const kindFor = (label: string, value: number): ScoreKind => {
      if (criteriaScore === 0) return ScoreKind.None;
      const passed =
        label === 'Overall score'
          ? practiceScore >= criteriaScore
          : value >= criteriaScore;
      return passed ? ScoreKind.Pass : ScoreKind.Fail;
    };

    const overallScore = {
      key: ScoreKey.Score,
      label: 'Overall score',
      value: practiceScore,
      weight: 100,
    };

    if (conversationalResults.callScoreCalcType === 'AVG') {
      return [
        {
          kind: kindFor(overallScore.label, overallScore.value),
          label: overallScore.label,
          max: '5',
          value: overallScore.value.toString(),
          labelKey: 'OVERALL_SCORE',
        },
      ];
    }

    const promptToKeyMap: Record<string, keyof IWeightAverage> = {
      SCORE_CUSTOMIZED: 'scoreCustomized',
      SCORE_SKILLS: 'scoreSkills',
      MESSAGE_ADHERENCE: 'messageAdherence',
      MESSAGE_ADHERENCE_V2: 'messageAdherence',
    };

    const labelForKey: Record<string, string> = {
      scoreCustomized: 'Objectives',
      messageAdherence: 'Message adherence',
      messageAdherenceV2: 'Message adherence V2',
      scoreSkills: 'Skills',
    };

    const weightAverage: IWeightAverage = {};
    for (const prompt of conversationalResults.promptTypes ?? []) {
      const key = promptToKeyMap[prompt];
      const scoreDetails = conversationalResults.weightAverage?.[key];
      if (key && scoreDetails) {
        weightAverage[key] = {
          weight: scoreDetails.weight,
          average: scoreDetails.average,
        };
      }
    }

    const scores = [
      overallScore,
      ...Object.entries(weightAverage).map(([key, { weight, average }]) => ({
        key: key as ScoreKey,
        label: labelForKey[key] ?? key,
        value: average,
        weight,
      })),
    ];

    const keyOrder: ScoreKey[] = [
      ScoreKey.Score,
      ScoreKey.MessageAdherence,
      ScoreKey.MessageAdherenceV2,
      ScoreKey.ScoreCustomized,
      ScoreKey.ScoreSkills,
    ];

    const keyMapping: Record<ScoreKey, string> = {
      [ScoreKey.Score]: 'SCORE_OVERALL',
      [ScoreKey.MessageAdherence]: 'MESSAGE_ADHERENCE',
      [ScoreKey.ScoreCustomized]: 'SCORE_CUSTOMIZED',
      [ScoreKey.ScoreSkills]: 'SCORE_SKILLS',
      [ScoreKey.MessageAdherenceV2]: 'MESSAGE_ADHERENCE_V2',
    };

    const isV2 = this.isMessageAdherenceV2();
    const filteredScores = scores.filter(({ key }) => {
      const effectiveKey =
        isV2 && key === ScoreKey.MessageAdherence
          ? ScoreKey.MessageAdherenceV2
          : (key as ScoreKey);
      return conversationalResults.promptTypes?.includes(
        keyMapping[effectiveKey],
      );
    });

    return filteredScores
      .sort(
        (a, b) =>
          keyOrder.indexOf(a.key as ScoreKey) -
          keyOrder.indexOf(b.key as ScoreKey),
      )
      .map(({ label, value, weight }) => ({
        kind: kindFor(label, value),
        label,
        max: '5',
        value: value.toString(),
        ...(label !== 'Overall score' && {
          description: this.localeService.formatPercentage(
            weight,
            this.localeService.getDecimalPlaces(weight, 2)
          ),
        }),
        labelKey: label.toUpperCase().replace(/\s+/g, '_'),
      }));
  }

  private toSkillScore(score: number | string): 'Expert' | 'Proficient' | 'Developing' {
    if (score === 'Expert' || score === 'Proficient' || score === 'Developing') return score;
    const n = typeof score === 'number' ? score : Number(score);
    if (n >= 5) return 'Expert';
    if (n >= 3) return 'Proficient';
    return 'Developing';
  }

  private async resolveAccuracyVisibility(): Promise<void> {
    this.accuracyFeedbackFlagEnabled.set(
      await this.featureFlagsService.isFlagEnabled(
        AppConstants.ACCURACY_FEEDBACK,
      ),
    );
  }

  private async fetchAccuracyFeedback(): Promise<void> {
    await this.accuracyFlagResolved;
    if (!this.accuracyFeedbackFlagEnabled() || !this.practiceCallUuid) return;
    if (this.accuracyFetchedForUuid === this.practiceCallUuid) return;
    this.accuracyFetchedForUuid = this.practiceCallUuid;

    this.accuracyState.set(AccuracyFeedbackState.Loading);
    this.accuracyClaims.set([]);

    this.resultsConversationalService
      .getAccuracyFeedback(this.practiceCallUuid)
      .pipe(take(1))
      .subscribe({
        next: ({ state, claims }) => {
          this.accuracyClaims.set(claims);
          this.accuracyState.set(state);
        },
        error: (err) => {
          this.accuracyClaims.set([]);
          this.accuracyState.set(AccuracyFeedbackState.Error);
          this.loggerService.error(
            '[ROLEPLAY RESULTS] fetchAccuracyFeedback failed',
            err,
          );
        },
      });
  }

  private fetchUserTranscript(): void {
    if (!this.practiceCallUuid) return;

    this.resultsConversationalService
      .getResultsConversationalTranscript(this.practiceCallUuid)
      .subscribe({
        next: (res: IUserTranscriptResponse) => {
          if (res?.userTranscript) {
            this.userTranscript.set(res);
          }
        },
        error: (err) => {
          this.loggerService.error(
            '[ROLEPLAY RESULTS] fetchUserTranscript failed',
            err,
          );
        },
      });
  }

  destroy(): void {
    this.stopPolling();
  }

  ngOnDestroy(): void {
    this.destroy();
  }
}
